// Versión del módulo contable: no fiscal (todo lo registrado) o fiscal (solo
// lo ya pasado). El backend decide si el tenant usa la función
// (varinicial.contabilidadfiscal) y si el rol está restringido a la fiscal;
// aquí solo se guarda la elección del usuario y se comparte entre pantallas.
//
// Estado a nivel de módulo + useSyncExternalStore: el selector vive en el
// sidebar y las pantallas en las rutas, sin tocar el contexto global.
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useMaterialUIController } from 'context';

export const VERSION_NO_FISCAL = 'N';
export const VERSION_FISCAL = 'F';

export const ETIQUETA_VERSION = {
  [VERSION_NO_FISCAL]: 'No fiscal',
  [VERSION_FISCAL]: 'Fiscal',
};

const ESTADO_INICIAL = {
  cargado: false,
  activa: false,
  version: VERSION_NO_FISCAL,
  soloFiscal: false,
  pendientes: 0,
  anoFiscal: null,
  mesFiscal: null,
};

let estado = ESTADO_INICIAL;
let usuarioCargado = null;
const oyentes = new Set();

function publicar(cambios) {
  estado = { ...estado, ...cambios };
  oyentes.forEach((fn) => fn());
}

function suscribir(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

const leerEstado = () => estado;

const claveGuardada = (usuario) => `contabilidad.version.${usuario || ''}`;

export function versionGuardada(usuario) {
  try {
    return localStorage.getItem(claveGuardada(usuario)) === VERSION_FISCAL
      ? VERSION_FISCAL
      : VERSION_NO_FISCAL;
  } catch (e) {
    return VERSION_NO_FISCAL;
  }
}

function guardarVersion(usuario, version) {
  try {
    localStorage.setItem(claveGuardada(usuario), version);
  } catch (e) {
    // sin almacenamiento (modo privado): la elección dura lo que la sesión
  }
}

/** Respuesta de contabilidadbackend_versioncontable → estado del selector. */
export function estadoDesdeRespuesta(data) {
  if (!data || !data.activa) {
    return { ...ESTADO_INICIAL, cargado: true };
  }
  return {
    cargado: true,
    activa: true,
    version: data.version === VERSION_FISCAL ? VERSION_FISCAL : VERSION_NO_FISCAL,
    soloFiscal: Boolean(data.solo_fiscal),
    pendientes: Number(data.pendientes) || 0,
    anoFiscal: data.anoinicialfiscal ?? null,
    mesFiscal: data.mesinicialfiscal ?? null,
  };
}

/** Solo para pruebas. */
export function reiniciarVersionContable() {
  estado = ESTADO_INICIAL;
  usuarioCargado = null;
}

export function useVersionContable() {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const actual = useSyncExternalStore(suscribir, leerEstado);

  const consultar = useCallback(
    async (version) => {
      if (!usuarioLog || !claveLog) return;
      try {
        const response = await fetch(`${servidor}/contabilidadbackend_versioncontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog,
            password: claveLog,
            version,
          }),
        });
        const data = await response.json();
        publicar(estadoDesdeRespuesta(response.ok ? data : null));
      } catch (e) {
        // sin respuesta: el módulo sigue como siempre (no fiscal, sin selector)
        publicar({ ...ESTADO_INICIAL, cargado: true });
      }
    },
    [servidor, usuarioLog, claveLog]
  );

  useEffect(() => {
    if (!usuarioLog || usuarioCargado === usuarioLog) return;
    usuarioCargado = usuarioLog;
    consultar(versionGuardada(usuarioLog));
  }, [usuarioLog, consultar]);

  const cambiarVersion = useCallback(
    (version) => {
      if (version !== VERSION_FISCAL && version !== VERSION_NO_FISCAL) return;
      if (estado.soloFiscal || version === estado.version) return;
      guardarVersion(usuarioLog, version);
      publicar({ version });
      consultar(version);
    },
    [usuarioLog, consultar]
  );

  return {
    ...actual,
    esFiscal: actual.activa && actual.version === VERSION_FISCAL,
    // La versión fiscal es de consulta: todo se registra en la no fiscal.
    soloConsulta: actual.activa && actual.version === VERSION_FISCAL,
    cambiarVersion,
    refrescar: () => consultar(estado.cargado ? estado.version : versionGuardada(usuarioLog)),
  };
}
